import React, { useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import * as Actions from "../../store/actions/contract/myContractDetailsActions";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useTranslation } from "react-i18next";
import "../common/DropdownCustomStyles.css";
import { Loader } from "@vds/loaders";
import styled from "styled-components";
import { useCountryListContext } from "../../contexts/CountryListContext";
import CPNotification from "../../pages/Login/CPNotification";
import {Wrapper} from "../../constants/common/StyledGlobalConstants";
import {COUNTRY_CODE_CONSTANTS} from "../../constants/StringConstants";
import {
  ContactContainer,
  ItemContainer,
  StarSpan,
  LabelContainer,
  ErrorContainer,
  FormContainer,
  CustomizedCountrySelect,
  CustomisedPhoneInput,
  PhoneContainer,
  CustomisedPrimeInput,
} from "./ContactMe.styles";
import { DEVICE_BREAKPOINTS } from "../../constants/appConstants";
import PropTypes from "prop-types";

const LoaderContainer = styled.div`
  div[aria-label="loader overlay"] {
    position: relative;
    width: 1.5rem;
    height: 1.5rem;
    left: 50%;
    > div {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
`;

const { TABLET } = DEVICE_BREAKPOINTS;
const NameRegex = /^[a-zA-Z'’]+( [a-zA-Z'’]+)*$/;
const EmailRegex =
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const ContactMeForm = (props) => {
  const { t } = useTranslation("contractsTranslation");
  const [contactName, setContactName] = useState({
    showError: false,
    isValid: false,
    errorText: "",
  });
  const [contactEmail, setContactEmail] = useState({
    showError: false,
    isValid: false,
    errorText: "",
  });
  const [contactPhone, setContactPhone] = useState({
    showError: false,
    isValid: false,
    isDefault: true,
    errorText: "",
    initialRender: true,
    inputAutoFocus: false,
    phoneNumber: "",
    formattedPhoneNumber: "",
  });
  const [selectedCountry, setSelectedCountry] = useState();
  
  const { disabled, windowSize } = props;

  const { accountUserTokenDetails } = useSelector((state) => state.ACCOUNT);
  const phoneUtil = PhoneNumberUtil.getInstance();
  const phoneInputField = useRef(null);

  const [isContactNameDisabled, setIsContactNameDisabled] = useState(disabled);

  const STR_DEFAULT_ADMIN = "defaultadmin";
  const normalizeName = (input) => input?.toLowerCase().replace(/\s+/g, "").trim();

  const isInvalidName = (name, isDefaultValue = false) => {
    const normalizedName = normalizeName(name);
    if (isDefaultValue) {
      return normalizedName === STR_DEFAULT_ADMIN;
    }
    return normalizedName === STR_DEFAULT_ADMIN || !NameRegex.test(name);
  };

  useEffect(() => {
    const handleInvalidName = () => {
      setIsContactNameDisabled(false);
      setContactName((prevState) => ({
        ...prevState,
        showError: true,
        isValid: false,
        errorText: t("Name is invalid"), // Error message for "default admin"
      }));
      props.setContractFormDetailsError("contactNameErr", true);
    };

    if (disabled && isInvalidName(props.fullName, true) && normalizeName(props.fullName) === normalizeName(props.contactName)) {
      handleInvalidName();
    } else if (!disabled && props.contactName && isInvalidName(props.contactName)) {
      handleInvalidName();
    } else if (props.fullName !== props.contactName) {
      setIsContactNameDisabled(false);
      if (props.contactName && isInvalidName(props.contactName)) {
        handleInvalidName();
      }
    }
  }, []);

 const { countryList, loading, error, fetchCountryList} = useCountryListContext();

 useEffect(() => {
  fetchCountryList(); // Fetch the country list
}, []); 
 
  useEffect(() => {
    let localSelectedCountry;
    if (accountUserTokenDetails?.ccode) {
      if (props?.contactPhoneNumber?.length > 0) {
        localSelectedCountry = countryList.filter((res) => res.countryCode === props.contactPhoneNumber.split(" ").shift());
      }
      if (!localSelectedCountry?.length) {
        localSelectedCountry = countryList.filter((res) => res.countryTwoLetterCode === accountUserTokenDetails?.ccode);
      }
    } else {
      localSelectedCountry = countryList;
    }
    setSelectedCountry(localSelectedCountry?.[0] || null);
  }, [countryList]);

  const toGetPhoneNumber = (inputNumber) => {
  if (!inputNumber || inputNumber.length === 0) {
    return "";
  }
  if (inputNumber.indexOf("+") < 0) {
    return inputNumber;
  }
  return phoneUtil.parseAndKeepRawInput(inputNumber).values_[2];
};

  useEffect(() => {
    if (props?.accountPhoneNumber) {
      setContactPhone((prevState) => {
        return {
          ...prevState,
          formattedPhoneNumber: toGetPhoneNumber(props.accountPhoneNumber),
        };
      });
    } else {
      setContactPhone((prevState) => {
        return {
          ...prevState,
          formattedPhoneNumber: "",
        };
      });
    }
  }, [props.accountPhoneNumber]);

  useEffect(() => {
    contactPhone.inputAutoFocus &&
      setTimeout(() => {
        phoneInputField.current.focus();
        setContactPhone((prevState) => {
          return {
            ...prevState,
            inputAutoFocus: false,
          };
        });
      });
  }, [contactPhone.inputAutoFocus]);

  const validPhoneNumberEntered = (phoneNum, country_Code) => {
    setContactPhone((prevState) => {
      return {
        ...prevState,
        showError: false,
        isValid: true,
        isDefault: false,
        phoneNumber: toFormatPhoneNumber(contactPhone.phoneNumber),
      };
    });
    props.setContractFormDetailsError("contactPhoneNumberErr", false);
    props.setRequestContractDetails("contactPhoneNumber", `${country_Code} ${phoneNum}`);
  };

  const isPhoneValid = (phone) => {
    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
      console.log("Phone validation error:", error);
      return false;
    }
  };

  useEffect(() => {
    disabled &&
      contactPhone.formattedPhoneNumber &&
      contactPhone.initialRender &&
      !contactPhone.phoneNumber &&
      setContactPhone((prevState) => {
        return {
          ...prevState,
          phoneNumber: contactPhone.formattedPhoneNumber,
        };
      });
    props.contactPhoneNumber &&
      contactPhone.initialRender &&
      !contactPhone.phoneNumber &&
      setContactPhone((prevState) => {
        return {
          ...prevState,
          phoneNumber: toGetPhoneNumber(props.contactPhoneNumber),
        };
      });
    if (disabled && contactPhone.initialRender && isPhoneValid(`${selectedCountry?.countryCode}${contactPhone.phoneNumber}`)) {
      validPhoneNumberEntered(contactPhone.phoneNumber, selectedCountry?.countryCode);
    } else if (!contactPhone.phoneNumber) {
        if (!contactPhone.initialRender) {
          setContactPhone((prevState) => {
            return {
              ...prevState,
              showError: true,
              errorText: t("Phone number is required"),
              isDefault: false,
              isValid: false,
            };
          });
          props.setContractFormDetailsError("contactPhoneNumberErr", true);
        }
      } else if (!isPhoneValid(`${selectedCountry?.countryCode}${contactPhone.phoneNumber}`)) {
        setContactPhone((prevState) => {
          return {
            ...prevState,
            showError: true,
            errorText: t("Phone number is invalid"),
            isDefault: false,
            isValid: false,
          };
        });
        props.setContractFormDetailsError("contactPhoneNumberErr", true);
      } else {
        validPhoneNumberEntered(contactPhone.phoneNumber, selectedCountry?.countryCode);
      }
  }, [contactPhone.phoneNumber, contactPhone.formattedPhoneNumber, selectedCountry]);

  const handleChange = (field, value) => {
    props.setRequestContractDetails(field, value);

    if (field === "contactName") {
      const name = normalizeName(value);
      const isDefaultAdmin = name === STR_DEFAULT_ADMIN;

      if (isDefaultAdmin) {
        setContactName((prevState) => {
          return {
            ...prevState,
            showError: true,
            isValid: false,
            errorText: t("Name is invalid"), // Error message for "default admin"
          };
        });
        props.setContractFormDetailsError("contactNameErr", true);
      } else if (NameRegex.test(value)) {
        setContactName((prevState) => {
          return {
            ...prevState,
            showError: false,
            isValid: true,
            errorText: "",
          };
        });
        props.setContractFormDetailsError("contactNameErr", false);
      } else {
        setContactName((prevState) => {
          return {
            ...prevState,
            showError: true,
            isValid: false,
            errorText: value === "" ? t("Name is required") : t("Name is invalid"),
          };
        });
        props.setContractFormDetailsError("contactNameErr", true);
      }
    } else if (field === "contactEmailAddress") {
      if (EmailRegex.test(value)) {
        setContactEmail((prevState) => {
          return {
            ...prevState,
            showError: false,
            isValid: true,
            errorText: "",
          };
        });
        props.setContractFormDetailsError("contactEmailAddressErr", false);
      } else {
        setContactEmail((prevState) => {
          return {
            ...prevState,
            showError: true,
            isValid: false,
            errorText: value === "" ? t("Email is required") : t("Email is invalid"),
          };
        });
        props.setContractFormDetailsError("contactEmailAddressErr", true);
      }
    }
  };

  const toFormatPhoneNumber = (inputValue) => {
    const input = inputValue.toString().replace(/\D/g, "").substring(0, 15);
    const areaCode = input.length === 8 ? input.substring(0, 1) : input.substring(0, 3);
    const middle = input.length === 8 ? input.substring(1, 4) : input.substring(3, 6);
    const last = input.length === 8 ? input.substring(4, 15) : input.substring(6, 15);
    return `${areaCode} ${middle} ${last}`;
  };

  const handlePhoneNumberChange = (event) => {
    event.preventDefault();
    setContactPhone((prevState) => {
      return {
        ...prevState,
        phoneNumber: event.target.value,
        initialRender: false,
      };
    });
  };

  const handleCountrySelectChange = (event) => {
    event.preventDefault();
    setSelectedCountry(event.value);
    setContactPhone((prevState) => {
      return {
        ...prevState,
        inputAutoFocus: true,
      };
    });
    !disabled &&
      setContactPhone((prevState) => {
        return {
          ...prevState,
          phoneNumber: "",
        };
      });
  };

  return (
     <LoaderContainer>
      <Loader active={loading} fullscreen={false} />
      {error && !loading && (
        <Wrapper marginTop="10px"> 
          <CPNotification isOpen={true} notificationType="error" height="auto" width="fit-content" closeButton={false}>
          {(COUNTRY_CODE_CONSTANTS.COUNTRY_CODE_FETCH_ERROR)}
          </CPNotification>
        </Wrapper>
      )}
      <FormContainer desktop={windowSize.width > TABLET}>
        <ContactContainer desktop={windowSize.width > TABLET} width="50%" marginTop={"24px"}>
          <ItemContainer>
            <LabelContainer>
              <label>{t("Name")}</label>
              <StarSpan disabled={isContactNameDisabled && props.fullName}>*</StarSpan>
            </LabelContainer>
            <div>
              <CustomisedPrimeInput
                id="my-contract-contact-name"
                required={contactName.showError}
                validGreen={contactName.isValid}
                aria-label={t("Contact name")}
                placeholder={t("Enter name")}
                defaultValue={props.contactName}
                width={"100%"}
                disabled={isContactNameDisabled && props.fullName}
                onChange={(e) => handleChange("contactName", e.target.value)}
                tabIndex="0"
              />
            </div>
            {contactName.showError && (
              <ErrorContainer role="alert" tabIndex="-1" aria-live="assertive">
                {contactName.errorText}
              </ErrorContainer>
            )}
          </ItemContainer>
        </ContactContainer>
        <ContactContainer desktop={windowSize.width > TABLET} width="50%" marginTop={windowSize.width >= TABLET ? "24px" : ""}>
          <ItemContainer>
            <LabelContainer>
              <label> {t("Phone number")}</label>
              <StarSpan>*</StarSpan>
            </LabelContainer>
            <PhoneContainer>
              <CustomizedCountrySelect
                key={`country-select-${countryList.length}`}
                value={selectedCountry}
                onChange={handleCountrySelectChange}
                options={countryList}
                optionLabel="countryCodeWithName"
                filter
                resetFilterOnHide
                valueTemplate={selectedCountry?.countryCode}
                id="my-contract-contact-phone-country-code"
                tabIndex="0"
                aria-label={t("contact phone country code")}
              />
              <CustomisedPhoneInput
                placeholder={t("Enter phone number")}
                value={contactPhone.phoneNumber}
                id="my-contract-contact-phone-number"
                onChange={handlePhoneNumberChange}
                validPhoneNumber={contactPhone.isValid}
                phoneNumberDefault={contactPhone.isDefault}
                autoFocus={contactPhone.inputAutoFocus}
                ref={phoneInputField}
                customWidth={windowSize.width > 1060}
                tabIndex="0"
                aria-label={t("contact phone number")}
              />
            </PhoneContainer>
            {contactPhone.showError && (
              <ErrorContainer role="alert" tabIndex="-1" aria-live="assertive">
                {contactPhone.errorText}
              </ErrorContainer>
            )}
          </ItemContainer>
        </ContactContainer>
      </FormContainer>
      <ContactContainer desktop={windowSize.width > TABLET} marginTop="32px">
        <ItemContainer desktop={windowSize.width > TABLET}>
          <LabelContainer>
            <label>{t("Email")}</label>
            <StarSpan disabled={disabled && props.accountEmailId}>*</StarSpan>
          </LabelContainer>
          <div>
            <CustomisedPrimeInput
              id="my-contract-contact-email"
              required={contactEmail.showError}
              validGreen={contactEmail.isValid}
              aria-label={t("Contact email")}
              placeholder={t("Enter email")}
              defaultValue={props.contactEmailAddress}
              width={"100%"}
              disabled={disabled && props.accountEmailId}
              onChange={(e) => handleChange("contactEmailAddress", e.target.value)}
              tabIndex="0"
            />
          </div>
          {contactEmail.showError && (
            <ErrorContainer role="alert" tabIndex="-1" aria-live="assertive">
              {contactEmail.errorText}
            </ErrorContainer>
          )}
        </ItemContainer>
      </ContactContainer>
    </LoaderContainer>
  );
};

const mapDispatchToProps = {
  setRequestContractDetails: Actions.setRequestContractDetails,
  setContractFormDetailsError: Actions.setContractFormDetailsError,
};
const mapStateToProps = (state) => {
  return {
    contactName: state.CONTRACT.contactName,
    contactEmailAddress: state.CONTRACT.contactEmailAddress,
    contactPhoneNumber: state.CONTRACT.contactPhoneNumber,
    contactNameErr: state.CONTRACT.contactNameErr,
    contactEmailAddressErr: state.CONTRACT.contactEmailAddressErr,
    contactPhoneNumberErr: state.CONTRACT.contactPhoneNumberErr,
    windowSize: state.ACCOUNT.windowSize,
    fullName: state.ACCOUNT.fullName,
    accountEmailId: state.CONTRACT.accountEmailId,
    accountPhoneNumber: state.CONTRACT.accountPhoneNumber,
    userLanguage: state.ACCOUNT.userLanguage,
  };
};

ContactMeForm.propTypes = {
  disabled: PropTypes.bool,
  windowSize: PropTypes.object,
  setRequestContractDetails: PropTypes.func,
  setContractFormDetailsError: PropTypes.func,
  contactName: PropTypes.string,
  contactEmailAddress: PropTypes.string,
  contactPhoneNumber: PropTypes.string,
  fullName: PropTypes.string,
  accountEmailId: PropTypes.string,
  accountPhoneNumber: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactMeForm);